import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import PeopleIcon from '@material-ui/icons/People';
import StarIcon from '@material-ui/icons/Star';
import LanguageIcon from '@material-ui/icons/Language';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import LocalDiningIcon from '@material-ui/icons/LocalDining';
import HotelIcon from '@material-ui/icons/Hotel';
import PlaceIcon from '@material-ui/icons/Place';
import LocationPlatformHeader from './LocationPlatformHeader';
import {
  GOOGLE_MAPS_API_KEY,
  formatGuests,
  formatRating,
  getHeroImage,
  getLocationAddressLabel,
  getLocationCity,
  getLocationPosition,
} from './locationPlatformUtils';

const Page = styled.div`
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(221, 151, 148, 0.18), transparent 34vw),
    linear-gradient(180deg, #fffaf6 0%, #f7efea 48%, #fffaf6 100%);
  color: #352b27;
  font-family: Quicksand, sans-serif;
`;

const Hero = styled.section`
  max-width: 1180px;
  min-height: 500px;
  margin: 24px auto 0;
  border: 1px solid rgba(217, 194, 183, 0.75);
  border-radius: 22px;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  background-image: ${({ src }) =>
    `linear-gradient(90deg, rgba(24, 18, 15, 0.72), rgba(24, 18, 15, 0.46) 46%, rgba(24, 18, 15, 0.36)), linear-gradient(180deg, rgba(24, 18, 15, 0.26), rgba(24, 18, 15, 0.78)), url('${src}')`};
  background-position: center;
  background-size: cover;
  box-shadow: 0 24px 70px rgba(72, 49, 38, 0.16);

  @media (max-width: 1220px) {
    margin: 18px 18px 0;
  }

  @media (max-width: 720px) {
    min-height: 470px;
    border-radius: 18px;
  }
`;

const HeroInner = styled.div`
  width: 100%;
  max-width: 1180px;
  margin: 0 auto;
  padding: 90px 28px 42px;
  color: white;

  h1 {
    max-width: 860px;
    margin: 0 0 12px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 56px;
    font-weight: 400;
    line-height: 1.05;
    letter-spacing: 0;
    text-shadow: 0 3px 22px rgba(0, 0, 0, 0.52);
  }

  p {
    max-width: 760px;
    margin: 0;
    font-size: 18px;
    line-height: 1.55;
    text-shadow: 0 2px 16px rgba(0, 0, 0, 0.62);
  }

  @media (max-width: 720px) {
    padding: 70px 18px 32px;

    h1 {
      font-size: 36px;
    }
  }
`;

const Content = styled.main`
  max-width: 1180px;
  margin: 0 auto;
  padding: 30px 28px 72px;

  @media (max-width: 720px) {
    padding: 26px 18px 46px;
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.section`
  margin-bottom: 20px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(224, 207, 198, 0.88);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(79, 56, 44, 0.07);

  h2 {
    margin: 0 0 14px;
    color: #3a2c28;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 28px;
    font-weight: 400;
    letter-spacing: 0;
  }

  p {
    margin: 0;
    color: #4f4541;
    font-size: 16px;
    line-height: 1.7;
  }

  @media (max-width: 720px) {
    padding: 18px;
    border-radius: 14px;
  }
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 22px 0 0;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const MetaItem = styled.div`
  min-height: 86px;
  padding: 14px;
  background: rgba(255, 250, 246, 0.96);
  border: 1px solid rgba(255, 255, 255, 0.82);
  border-radius: 14px;
  box-shadow: 0 18px 42px rgba(25, 17, 13, 0.28);
  color: #31231f;

  span {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #5f343a;
    font-size: 13px;
    font-weight: 700;
  }

  strong {
    display: block;
    margin-top: 8px;
    color: #2a201d;
    font-size: 17px;
    font-weight: 800;
    line-height: 1.35;
  }

  svg {
    width: 17px;
    height: 17px;
    color: #9f555d;
  }
`;

const PriceCard = styled.div`
  display: grid;
  gap: 18px;
`;

const PriceHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;

  @media (max-width: 680px) {
    flex-direction: column;
  }
`;

const PriceValue = styled.div`
  strong {
    display: block;
    color: #31231f;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 38px;
    font-weight: 400;
    line-height: 1.05;
  }

  span {
    display: block;
    margin-top: 6px;
    color: #5b463f;
    font-size: 15px;
    font-weight: 700;
  }
`;

const PriceBadge = styled.div`
  min-width: 128px;
  padding: 10px 14px;
  background: #f4dfdc;
  border: 1px solid #e6c8c5;
  border-radius: 999px;
  color: #783d45;
  font-size: 14px;
  font-weight: 800;
  text-align: center;
`;

const PriceScale = styled.div`
  position: relative;
  padding-top: 20px;
`;

const PriceSegments = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  overflow: hidden;
  height: 16px;
  border-radius: 999px;
  background: #eadbd4;
`;

const PriceSegment = styled.div`
  background: ${({ color }) => color};
`;

const PriceMarker = styled.div`
  position: absolute;
  left: ${({ position }) => `${position}%`};
  top: 5px;
  width: 2px;
  height: 34px;
  background: #2f2420;
  transform: translateX(-1px);

  &:before {
    content: '';
    position: absolute;
    top: -7px;
    left: 50%;
    width: 14px;
    height: 14px;
    border: 3px solid #ffffff;
    border-radius: 50%;
    background: #9f555d;
    box-shadow: 0 5px 14px rgba(47, 36, 32, 0.28);
    transform: translateX(-50%);
  }
`;

const PriceLabels = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 10px;
  color: #5b463f;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
`;

const PriceDrivers = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  span {
    padding: 7px 10px;
    background: #fff7f4;
    border: 1px solid #eadbd4;
    border-radius: 999px;
    color: #5c4740;
    font-size: 13px;
    font-weight: 800;
  }
`;

const Gallery = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 12px;

  img {
    width: 100%;
    height: 210px;
    object-fit: cover;
    border-radius: 14px;
  }

  img:first-child {
    height: 430px;
    grid-row: span 2;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;

    img,
    img:first-child {
      height: 240px;
      grid-row: auto;
    }
  }
`;

const List = styled.ul`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    padding: 15px;
    background: #fffdfb;
    border: 1px solid #eadbd4;
    border-radius: 14px;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const FactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const FactBox = styled.div`
  padding: 14px;
  background: #fffdfb;
  border: 1px solid #eadbd4;
  border-radius: 14px;

  span {
    display: block;
    margin-bottom: 6px;
    color: #6b4f48;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
  }

  strong {
    display: block;
    font-size: 16px;
    line-height: 1.35;
  }
`;

const IconFact = styled(FactBox)`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr);
  gap: 10px;
  align-items: start;

  svg {
    width: 22px;
    height: 22px;
    padding: 7px;
    background: #f4dfdc;
    border-radius: 12px;
    color: #9f555d;
  }
`;

const ExperienceList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ExperienceBox = styled.div`
  padding: 16px;
  background: #fffdfb;
  border: 1px solid #eadbd4;
  border-radius: 14px;

  h3 {
    margin: 0 0 8px;
    font-size: 17px;
    letter-spacing: 0;
  }
`;

const ConceptSection = styled(Section)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.92), rgba(250, 239, 234, 0.92));
`;

const ConceptGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const ConceptCard = styled.article`
  padding: 18px;
  min-height: 190px;
  background: #fffdfb;
  border: 1px solid #eadbd4;
  border-radius: 16px;

  h3 {
    margin: 0 0 10px;
    color: #3a2c28;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 23px;
    font-weight: 400;
  }

  p {
    margin: 0 0 12px;
  }

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 11px;
    background: #f7e9e5;
    border-radius: 999px;
    color: #9b5f63;
    font-size: 13px;
    font-weight: 700;
  }
`;

const Accordion = styled.div`
  display: grid;
  gap: 10px;
`;

const AccordionItem = styled.div`
  overflow: hidden;
  background: #fffdfb;
  border: 1px solid #eadbd4;
  border-radius: 14px;
`;

const AccordionButton = styled.button`
  width: 100%;
  min-height: 58px;
  padding: 0 16px;
  border: 0;
  background: transparent;
  color: #3a2c28;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  cursor: pointer;
  font-family: Quicksand, sans-serif;
  font-size: 16px;
  font-weight: 800;
  text-align: left;

  svg {
    flex: 0 0 auto;
    color: #c9858a;
    transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
    transition: transform 180ms ease;
  }
`;

const AccordionPanel = styled.div`
  display: ${({ open }) => (open ? 'block' : 'none')};
  padding: 0 16px 16px;

  p {
    margin: 0;
  }
`;

const MapBox = styled.div`
  overflow: hidden;
  height: 320px;
  border: 1px solid #eadbd4;
  border-radius: 16px;
  background: #f4ede8;
`;

const MapHint = styled.div`
  padding-top: 12px;
  color: #55433d;
  font-size: 14px;
  line-height: 1.5;
`;

const Aside = styled.aside`
  position: sticky;
  top: 88px;
  align-self: start;
  padding: 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(224, 207, 198, 0.88);
  border-radius: 16px;
  box-shadow: 0 18px 48px rgba(79, 56, 44, 0.09);

  h2 {
    margin: 0 0 10px;
    font-family: Georgia, 'Times New Roman', serif;
    font-size: 24px;
    font-weight: 400;
    letter-spacing: 0;
  }

  p {
    margin: 0 0 16px;
    color: #5c504b;
    font-size: 14px;
    line-height: 1.5;
  }

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 46px;
    margin-top: 10px;
    padding: 0 14px;
    border-radius: 6px;
    background: #d48b8f;
    color: white;
    font-weight: 700;
    text-align: center;
    text-decoration: none;
  }

  a.secondary {
    background: #f4efec;
    color: #3d332f;
  }

  @media (max-width: 900px) {
    position: static;
  }
`;

const Loading = styled.div`
  padding: 70px 28px;
  color: #725f55;
  font-weight: 700;
`;

const markerIcon = {
  url:
    'data:image/svg+xml;charset=UTF-8,' +
    encodeURIComponent(
      '<svg xmlns="http://www.w3.org/2000/svg" width="48" height="56" viewBox="0 0 48 56"><path fill="#a85f66" stroke="#fff" stroke-width="3" d="M24 3C13.5 3 5 11.5 5 22c0 14.2 19 31 19 31s19-16.8 19-31C43 11.5 34.5 3 24 3Z"/><path fill="#fff7f4" d="M24 29.5A7.5 7.5 0 1 0 24 14a7.5 7.5 0 0 0 0 15.5Z"/><path fill="#a85f66" d="M24 26.4c-2.5-2.1-5.1-4.2-5.1-7 0-1.8 1.4-3.2 3.2-3.2 1 0 1.8.4 2.4 1.1.6-.7 1.4-1.1 2.4-1.1 1.8 0 3.2 1.4 3.2 3.2 0 2.8-2.6 4.9-5.1 7l-.5.4-.5-.4Z"/></svg>'
    ),
};

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true,
  styles: [
    { elementType: 'geometry', stylers: [{ color: '#f4ede8' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#5c4a43' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#fffaf6' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#d7c9c0' }] },
  ],
};

const formatCurrency = value => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
  return `${Number(value).toLocaleString('de-DE')} €`;
};

const getPriceMarkerPosition = priceEstimate => {
  const min = Number(priceEstimate?.benchmarkRange?.min || 70);
  const max = Number(priceEstimate?.benchmarkRange?.max || 320);
  const rangeMin = Number(priceEstimate?.estimatedPerPerson?.min || min);
  const rangeMax = Number(priceEstimate?.estimatedPerPerson?.max || rangeMin);
  const midpoint = (rangeMin + rangeMax) / 2;
  if (max <= min) return 50;
  return Math.max(4, Math.min(96, ((midpoint - min) / (max - min)) * 100));
};

const LocationProfilePage = () => {
  const { slug } = useParams();
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openQuestionIndex, setOpenQuestionIndex] = useState(0);

  useEffect(() => {
    const loadLocation = async () => {
      setLoading(true);
      const response = await axios.get('location-read/', { params: { slug } });
      const locations = response.data.locations || [];
      const matchingLocation = locations.find(item => item.seo && item.seo.slug === slug);
      if (matchingLocation || locations.length === 1) {
        setLocation(matchingLocation || locations[0]);
        setLoading(false);
        return;
      }

      const totalPages = response.data.pagination && response.data.pagination.totalPages ? response.data.pagination.totalPages : 0;
      for (let requestedPage = 1; requestedPage < Math.min(totalPages, 30); requestedPage += 1) {
        const pagedResponse = await axios.get('location-read/', { params: { slug, requestedPage } });
        const pagedLocations = pagedResponse.data.locations || [];
        const pagedMatchingLocation = pagedLocations.find(item => item.seo && item.seo.slug === slug);
        if (pagedMatchingLocation) {
          setLocation(pagedMatchingLocation);
          setLoading(false);
          return;
        }
      }

      setLocation(null);
      setLoading(false);
    };
    loadLocation();
  }, [slug]);

  if (loading) {
    return (
      <Page>
        <LocationPlatformHeader />
        <Loading>Location wird geladen</Loading>
      </Page>
    );
  }

  if (!location) {
    return (
      <Page>
        <LocationPlatformHeader />
        <Loading>Diese Hochzeitslocation wurde nicht gefunden. <Link to="/hochzeitslocations/">Zur Suche</Link></Loading>
      </Page>
    );
  }

  const rating = formatRating(location);
  const image = getHeroImage(location);
  const city = getLocationCity(location);
  const introText = location.editorialIntro || location.subtitle || location.seo?.metaDescription || location.description;
  const faqItems = location.faqAccordion || location.contentChunks || [];
  const position = getLocationPosition(location);
  const priceMin = formatCurrency(location.priceEstimate?.estimatedPerPerson?.min);
  const priceMax = formatCurrency(location.priceEstimate?.estimatedPerPerson?.max);
  const priceRangeLabel = priceMin && priceMax ? `ca. ${priceMin}-${priceMax} pro Person` : location.priceEstimate?.summary;
  const priceMarkerPosition = getPriceMarkerPosition(location.priceEstimate);
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'EventVenue',
    name: location.title,
    description: location.seo?.metaDescription || location.description,
    image: location.images?.map(item => item.src).filter(Boolean),
    address: getLocationAddressLabel(location),
    url: `${window.location.origin}/hochzeitslocation/${slug}/`,
    maximumAttendeeCapacity: location.maximumNumberOfGuests || undefined,
    aggregateRating:
      location.bestRating && location.bestRating.value
        ? {
            '@type': 'AggregateRating',
            ratingValue: location.bestRating.value,
            reviewCount: location.bestRating.count || undefined,
          }
        : undefined,
  };

  return (
    <Page>
      <Helmet>
        <title>{location.seo?.title || `${location.title} | Hochzeitslocation`}</title>
        <meta name="description" content={location.seo?.metaDescription || location.description} />
        <meta property="og:title" content={location.seo?.title || location.title} />
        <meta property="og:description" content={location.seo?.metaDescription || location.description} />
        <meta property="og:image" content={image} />
        <link rel="canonical" href={`${window.location.origin}/hochzeitslocation/${slug}/`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <LocationPlatformHeader />
      <Hero src={image}>
        <HeroInner>
          <h1>{location.title}</h1>
          <p>{introText}</p>
          <MetaGrid>
            <MetaItem>
              <span>
                <LocationOnIcon /> Ort
              </span>
              <strong>{city || getLocationAddressLabel(location) || 'auf Anfrage'}</strong>
            </MetaItem>
            <MetaItem>
              <span>
                <PeopleIcon /> Gäste
              </span>
              <strong>{formatGuests(location)}</strong>
            </MetaItem>
            <MetaItem>
              <span>
                <StarIcon /> Bewertungen
              </span>
              <strong>{rating || 'noch nicht bewertet'}</strong>
            </MetaItem>
          </MetaGrid>
        </HeroInner>
      </Hero>
      <Content>
        <Layout>
          <div>
            {location.images && location.images.length > 1 && (
              <Section>
                <Gallery>
                  {location.images.slice(0, 5).map((item, index) => (
                    <img
                      key={item.id || index}
                      src={item.src || item.url}
                      alt={`${location.title} Bild ${index + 1}`}
                      onError={event => {
                        event.currentTarget.src = '/assets/location-search/background.jpg';
                      }}
                    />
                  ))}
                </Gallery>
              </Section>
            )}
            <Section>
              <h2>Profil</h2>
              <p>{introText}</p>
            </Section>
            {location.weddingConceptHighlights && location.weddingConceptHighlights.length > 0 && (
              <ConceptSection>
                <h2>Ideen für eure Hochzeit</h2>
                <ConceptGrid>
                  {location.weddingConceptHighlights.map((highlight, index) => (
                    <ConceptCard key={index}>
                      <h3>{highlight.title}</h3>
                      <p>{highlight.description}</p>
                      {highlight.trendConnection && (
                        <span>
                          <FavoriteBorderIcon /> {highlight.trendConnection}
                        </span>
                      )}
                    </ConceptCard>
                  ))}
                </ConceptGrid>
              </ConceptSection>
            )}
            {location.priceEstimate && (
              <Section>
                <h2>Preis-Einschätzung</h2>
                <PriceCard>
                  <PriceHeader>
                    <PriceValue>
                      <strong>{priceRangeLabel}</strong>
                      <span>{location.priceEstimate.summary || 'Orientierung für Menü, Getränke und Hochzeitsformat.'}</span>
                    </PriceValue>
                    <PriceBadge>{location.priceEstimate.marketPosition || location.priceEstimate.level}</PriceBadge>
                  </PriceHeader>
                  <PriceScale aria-label={`Preisniveau ${location.priceEstimate.marketPosition || location.priceEstimate.level}`}>
                    <PriceMarker position={priceMarkerPosition} />
                    <PriceSegments>
                      <PriceSegment color="#d7eadb" />
                      <PriceSegment color="#f2e5bc" />
                      <PriceSegment color="#e9c4a6" />
                      <PriceSegment color="#d69aa0" />
                    </PriceSegments>
                    <PriceLabels>
                      <span>günstig</span>
                      <span>mittel</span>
                      <span>gehoben</span>
                      <span>Premium</span>
                    </PriceLabels>
                  </PriceScale>
                  {location.priceEstimate.priceDrivers && location.priceEstimate.priceDrivers.length > 0 && (
                    <PriceDrivers>
                      {location.priceEstimate.priceDrivers.map(driver => (
                        <span key={driver}>{driver}</span>
                      ))}
                    </PriceDrivers>
                  )}
                </PriceCard>
              </Section>
            )}
            {location.searchFacets && (
              <Section>
                <h2>Such- und Filterdaten</h2>
                <FactGrid>
                  <IconFact>
                    <PlaceIcon />
                    <div>
                      <span>Stil</span>
                      <strong>{(location.searchFacets.styles || []).join(', ') || 'auf Anfrage'}</strong>
                    </div>
                  </IconFact>
                  <IconFact>
                    <FavoriteBorderIcon />
                    <div>
                      <span>Ideal für</span>
                      <strong>{(location.searchFacets.bestFor || []).join(', ') || 'Hochzeiten'}</strong>
                    </div>
                  </IconFact>
                  <IconFact>
                    <LocalDiningIcon />
                    <div>
                      <span>Catering</span>
                      <strong>{location.searchFacets.catering || 'auf Anfrage'}</strong>
                    </div>
                  </IconFact>
                  <IconFact>
                    <LocationOnIcon />
                    <div>
                      <span>Außenbereich</span>
                      <strong>{location.searchFacets.outdoorArea || 'auf Anfrage'}</strong>
                    </div>
                  </IconFact>
                  <IconFact>
                    <HotelIcon />
                    <div>
                      <span>Übernachtung</span>
                      <strong>{location.searchFacets.accommodation || 'auf Anfrage'}</strong>
                    </div>
                  </IconFact>
                  <IconFact>
                    <StarIcon />
                    <div>
                      <span>Planungsgefühl</span>
                      <strong>{location.searchFacets.planningEffort || 'mittel'}</strong>
                    </div>
                  </IconFact>
                </FactGrid>
              </Section>
            )}
            {location.highlights && location.highlights.length > 0 && (
              <Section>
                <h2>Highlights</h2>
                <List>
                  {location.highlights.map((highlight, index) => (
                    <li key={index}>
                      <strong>{highlight.text}</strong>
                      {highlight.subText && <p>{highlight.subText}</p>}
                    </li>
                  ))}
                </List>
              </Section>
            )}
            {location.experienceSummary && (
              <Section>
                <h2>Erfahrungen</h2>
                <ExperienceList>
                  {(location.experienceSummary.positive || []).map((item, index) => (
                    <ExperienceBox key={`positive-${index}`}>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </ExperienceBox>
                  ))}
                  {(location.experienceSummary.watchouts || []).map((item, index) => (
                    <ExperienceBox key={`watchout-${index}`}>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </ExperienceBox>
                  ))}
                </ExperienceList>
              </Section>
            )}
            {faqItems && faqItems.length > 0 && (
              <Section>
                <h2>Planungsfragen</h2>
                <Accordion>
                  {faqItems.map((chunk, index) => (
                    <AccordionItem key={index}>
                      <AccordionButton
                        type="button"
                        open={openQuestionIndex === index}
                        aria-expanded={openQuestionIndex === index}
                        onClick={() => setOpenQuestionIndex(openQuestionIndex === index ? -1 : index)}
                      >
                        {chunk.question}
                        <KeyboardArrowDownIcon />
                      </AccordionButton>
                      <AccordionPanel open={openQuestionIndex === index}>
                        <p>{chunk.answer}</p>
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Section>
            )}
            {location.rooms && location.rooms.length > 0 && (
              <Section>
                <h2>Räume und Kapazitäten</h2>
                <List>
                  {location.rooms.slice(0, 6).map((room, index) => (
                    <li key={index}>
                      <strong>{room.roomname || `Raum ${index + 1}`}</strong>
                      <p>
                        {room.maxCapacitySitting ? `${room.maxCapacitySitting} sitzend` : ''}
                        {room.maxCapacityStanding ? `, ${room.maxCapacityStanding} stehend` : ''}
                        {room.squaremeters ? `, ${room.squaremeters} m²` : ''}
                      </p>
                    </li>
                  ))}
                </List>
              </Section>
            )}
            {position && (
              <Section>
                <h2>Lage</h2>
                <MapBox>
                  <LoadScript id="location-profile-map" googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
                    <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} center={position} zoom={14} options={mapOptions}>
                      <Marker position={position} icon={markerIcon} title={location.title} />
                    </GoogleMap>
                  </LoadScript>
                </MapBox>
                <MapHint>{getLocationAddressLabel(location)}</MapHint>
              </Section>
            )}
          </div>
          <Aside>
            <h2>Location anfragen</h2>
            <p>Sendet eure Anfrage direkt an die Location oder merkt euch das Profil für eure engere Auswahl.</p>
            {location.website && (
              <a href={location.website} target="_blank" rel="noopener noreferrer">
                <LanguageIcon /> Anbieter-Website
              </a>
            )}
            <a className="secondary" href={`mailto:locations@eurehochzeitonline.com?subject=Profil beanspruchen: ${encodeURIComponent(location.title)}`}>
              Profil beanspruchen
            </a>
            <a className="secondary" href={`mailto:locations@eurehochzeitonline.com?subject=Anfrage: ${encodeURIComponent(location.title)}`}>
              Anfrage senden
            </a>
          </Aside>
        </Layout>
      </Content>
    </Page>
  );
};

export default LocationProfilePage;
